/**
 * Makes user-authored mail readable on whichever surface it lands on.
 *
 * Mail from Outlook carries the sender's own colours, chosen against the paper
 * they were writing on. A signature set in `rgb(68, 68, 68)` has a contrast
 * ratio of 1.51 against our dark ticket panel and is effectively invisible;
 * a sender writing in Outlook's own dark mode sends white text that vanishes
 * against our light one. Both are the same bug seen from two sides.
 *
 * This runs in the browser rather than in the purifier because only the browser
 * knows the answer to the two questions that matter: what colour did this
 * element *actually* end up with once inline styles, `<font color>`, the mail's
 * own `<style>` block and inheritance had all been resolved, and what is it
 * *actually* sitting on. `getComputedStyle` answers both, and the stored HTML
 * is left exactly as the sender wrote it.
 *
 * This replaces four `str_replace` calls that deleted two literal colour
 * strings — they matched `color:rgb(68,68,68);` but not the spaced form Outlook
 * actually sends, and nothing else at all.
 */

import {
  compositeOver,
  parseCssColor,
  repairColor,
  toHex,
  type Rgb,
  type Rgba,
} from '@/utils/contrast'
import { isDarkMode } from '@/utils/theme'

/**
 * Every wrapper that holds mail as the sender wrote it: the ticket description,
 * each message in the thread, and both message lists in the user portal.
 */
const MAIL_BODY_SELECTOR = '.hit-message-body'

/** The app's own panels, for the case where no ancestor paints a background. */
const FALLBACK_SURFACE: Record<'dark' | 'light', Rgb> = {
  dark: [31, 41, 55],
  light: [255, 255, 255],
}

type Repair = {
  element: HTMLElement
  color: string
}

const isElement = (node: Node | null): node is HTMLElement =>
  node !== null && node.nodeType === Node.ELEMENT_NODE

/** Our own panels, for the case where no ancestor paints anything opaque. */
const fallbackSurface = (): Rgb =>
  FALLBACK_SURFACE[isDarkMode() ? 'dark' : 'light']

/**
 * Is there an image painted under this element?
 *
 * The one background that cannot be measured: there is no single colour to
 * hold the text against, so text over it is left as the sender wrote it. The
 * inline value is read as well as the computed one because jsdom, which the
 * unit tests run in, resolves very little of the latter.
 */
function paintsImage(element: Element): boolean {
  return [
    window.getComputedStyle(element).backgroundImage,
    (element as HTMLElement).style.backgroundImage,
  ].some((image) => Boolean(image) && image !== 'none')
}

/**
 * The colour painted on this element itself, or `null` for none.
 *
 * HTMLPurifier rewrites `bgcolor` into an inline style, and browsers map a
 * leftover one to `background-color` anyway; reading the attribute directly
 * costs nothing and does not depend on either staying true.
 */
function backgroundOf(element: Element): Rgba | null {
  const computed = parseCssColor(
    window.getComputedStyle(element).backgroundColor,
  )

  if (computed && computed[3] > 0) {
    return computed
  }

  return parseCssColor(element.getAttribute('bgcolor'))
}

/**
 * Did the *sender* paint this element, as opposed to our stylesheet?
 *
 * Not the same as "is inside the mail": our own rules paint things inside the
 * mail too — `.user-edited-content blockquote` gets `dark:bg-white/5`, and a
 * quoted reply is exactly where Outlook's `#1F497D` header sits. Only an inline
 * style or a `bgcolor` attribute came from the sender.
 */
function authorPaints(element: Element): boolean {
  if (element.hasAttribute('bgcolor')) {
    return true
  }

  const inline = (element as HTMLElement).style

  return inline.backgroundColor !== '' || inline.background !== ''
}

/**
 * Is the colour this element ended up with the one the sender wrote?
 *
 * Where a sender chose both the text colour and the background — white on a
 * branded banner, their own faint footer on their own grey table — the pair is
 * theirs and is left exactly as written; a `#b0adc5` footer on `#edf2f7` reads
 * here as it does in every other mail client. But sitting on their background
 * does not make the text colour theirs. Our `[&_a]:dark:text-violet-300` is
 * `!important` and repaints every link — light violet on Gmail's light grey
 * Drive chip (ticket 11014) — and text they left uncoloured inherits our body
 * colour.
 *
 * So this walks up to the nearest colour the sender declared, an inline
 * `color` or a `<font color>`, and compares it with the computed one. They
 * differ where our stylesheet won, and there is none to find where the colour
 * comes from the page. A declaration that cannot be parsed gets the benefit of
 * the doubt.
 */
function senderColourInEffect(element: HTMLElement, body: Element): boolean {
  let node: Element | null = element

  while (isElement(node) && node !== body) {
    const declared = node.style.color || node.getAttribute('color')

    if (declared) {
      const sender = parseCssColor(declared)
      const actual = parseCssColor(window.getComputedStyle(element).color)

      if (!sender || !actual) {
        return true
      }

      return [0, 1, 2].every(
        (channel) => Math.abs(sender[channel] - actual[channel]) <= 1,
      )
    }

    node = node.parentElement
  }

  return false
}

/** Flattens translucent layers, outermost first, onto an opaque base. */
function flatten(layers: Rgba[], base: Rgb): Rgb {
  return layers.reduceRight(
    (surface, layer) => compositeOver(layer, surface),
    base,
  )
}

/** What a piece of text sits on, and whether the sender painted it. */
type Surface = {
  color: Rgb
  paintedBySender: boolean
}

/**
 * What this element is really painted on, or `null` when that cannot be known
 * and the element must be left alone: an image under it has no one colour.
 *
 * A background the sender set is measured like any other rather than ending
 * the pass. Whether the text on it is theirs to keep is `decide`'s question.
 *
 * Translucent layers are composited rather than treated as the answer, so a
 * quoted block measures against the tint it really carries.
 */
function surfaceUnder(element: HTMLElement, body: Element): Surface | null {
  const layers: Rgba[] = []
  let node: Element | null = element

  while (isElement(node)) {
    const insideMail = node !== body && body.contains(node)

    if (insideMail && paintsImage(node)) {
      return null
    }

    const background = backgroundOf(node)

    if (background && background[3] > 0) {
      if (background[3] === 1) {
        return {
          color: flatten(layers, [background[0], background[1], background[2]]),
          paintedBySender: insideMail && authorPaints(node),
        }
      }

      layers.push(background)
    }

    node = node.parentElement
  }

  return { color: flatten(layers, fallbackSurface()), paintedBySender: false }
}

/** Every element that actually paints text, each one only once. */
function textBearingElements(body: Element): HTMLElement[] {
  const walker = body.ownerDocument.createTreeWalker(body, NodeFilter.SHOW_TEXT)
  const seen = new Set<HTMLElement>()

  let node = walker.nextNode()
  while (node) {
    const parent = node.parentElement

    /**
     * Colour only matters where there is something to paint. Skipping the rest
     * cuts almost every node of an Outlook table layout out of the pass.
     */
    if (parent && node.nodeValue && node.nodeValue.trim().length > 0) {
      seen.add(parent)
    }

    node = walker.nextNode()
  }

  return [...seen]
}

function decide(element: HTMLElement, body: Element): Repair | null {
  if (element.dataset.mailContrastRepaired) {
    return null
  }

  const surface = surfaceUnder(element, body)
  if (!surface) {
    return null
  }

  if (surface.paintedBySender && senderColourInEffect(element, body)) {
    return null
  }

  const declared = parseCssColor(window.getComputedStyle(element).color)
  if (!declared) {
    return null
  }

  const foreground =
    declared[3] === 1
      ? ([declared[0], declared[1], declared[2]] as Rgb)
      : compositeOver(declared, surface.color)

  const repaired = repairColor(foreground, surface.color)

  return repaired ? { element, color: toHex(repaired) } : null
}

/**
 * Repairs the contrast of every mail body under `root`.
 *
 * Safe to call more than once, and safe to call on content that arrived after
 * the first pass: the history tab fetches the thread over XHR and hands it to
 * `v-html` long after the page has settled.
 */
export function repairMailContrast(root: ParentNode = document): void {
  const bodies = root.querySelectorAll<HTMLElement>(MAIL_BODY_SELECTOR)
  const repairs: Repair[] = []

  /**
   * Read everything first, then write. Interleaving `getComputedStyle` with
   * style writes forces a style recalculation per element, and a long thread
   * has thousands.
   */
  bodies.forEach(function (body) {
    textBearingElements(body).forEach(function (element) {
      const repair = decide(element, body)

      if (repair) {
        repairs.push(repair)
      }
    })
  })

  repairs.forEach(function ({ element, color }) {
    /**
     * Remember the inline colour and its priority — usually none — so a theme
     * flip can put the sender's own value back before measuring again.
     *
     * Written `!important` because our link colours are: `important: true`
     * emits every utility that way, and an inline declaration only outranks an
     * important one by being important itself.
     */
    element.dataset.mailColorBefore = element.style.color
    element.dataset.mailColorPriorityBefore =
      element.style.getPropertyPriority('color')
    element.dataset.mailContrastRepaired = 'yes'

    element.style.setProperty('color', color, 'important')
  })
}

/** Puts every repaired element back exactly as the sender wrote it. */
export function restoreMailContrast(root: ParentNode = document): void {
  root
    .querySelectorAll<HTMLElement>('[data-mail-contrast-repaired]')
    .forEach(function (element) {
      element.style.setProperty(
        'color',
        element.dataset.mailColorBefore || null,
        element.dataset.mailColorPriorityBefore ?? '',
      )

      delete element.dataset.mailColorBefore
      delete element.dataset.mailColorPriorityBefore
      delete element.dataset.mailContrastRepaired
    })
}

/**
 * Watches for the theme changing under the page.
 *
 * Picking light or dark from the menu reloads, so this only has real work to do
 * for the `system` setting, where the pre-paint script in
 * `partials/theme-mode.blade.php` flips the class live when the OS does.
 *
 * A `MutationObserver` rather than a listener on the `MediaQueryList`:
 * registering one of those hangs Pest's browser plugin, which is why the same
 * partial assigns `mq.onchange` instead.
 */
function watchThemeChanges(): void {
  let dark = isDarkMode()

  new MutationObserver(function () {
    if (isDarkMode() === dark) {
      return
    }

    dark = isDarkMode()
    restoreMailContrast()
    repairMailContrast()
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })
}

/** Runs the first pass as soon as the server-rendered markup is available. */
export function repairMailContrastWhenReady(): void {
  const start = (): void => {
    repairMailContrast()
    watchThemeChanges()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start)

    return
  }

  start()
}
